import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { GridPatternSVG } from "@/components/SVGElements";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: PhoneIcon,
    label: "Phone",
    value: "+91-9560121484",
    subValue: "0129-3693795",
    href: "tel:+919560121484",
  },
  {
    icon: EnvelopeIcon,
    label: "Email",
    value: "info@visionerconsultants.in",
    subValue: "support@visionerconsultants.in",
    href: "mailto:info@visionerconsultants.in",
  },
  {
    icon: MapPinIcon,
    label: "Office",
    value: "Gurugram, Haryana",
    subValue: "India - 122001",
    href: null,
  },
  {
    icon: ClockIcon,
    label: "Working Hours",
    value: "Mon - Sat: 9AM - 6PM",
    subValue: "Sunday: Closed",
    href: null,
  },
];

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    if (formData.message.length < 10) {
      toast.error("Message must be at least 10 characters");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-zinc-50"
    >
      <Navbar />
      <main className="pt-20">
        {/* Page Header */}
        <section className="py-20 bg-slate-900 relative overflow-hidden">
          <div className="absolute inset-0 text-white opacity-10">
            <GridPatternSVG />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider font-manrope">
                Get in Touch
              </span>
              <h1 className="font-oswald text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4">
                Let's Build <span className="text-orange-500">Together</span>
              </h1>
              <p className="text-slate-300 mt-6 text-lg font-manrope max-w-3xl mx-auto">
                Ready to start your project? Contact us today for a free consultation
                and quote.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Content */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-zinc-50 border border-slate-100 p-8 sm:p-10"
              >
                <h2 className="font-oswald text-2xl font-semibold text-slate-900 mb-6">
                  Send us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-slate-700 mb-2 font-manrope"
                    >
                      Your Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full rounded-none border-slate-200 focus:border-orange-500 focus:ring-orange-500"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-slate-700 mb-2 font-manrope"
                    >
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full rounded-none border-slate-200 focus:border-orange-500 focus:ring-orange-500"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-slate-700 mb-2 font-manrope"
                    >
                      Your Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={6}
                      placeholder="Tell us about your project..."
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full rounded-none border-slate-200 focus:border-orange-500 focus:ring-orange-500 resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-slate-900 hover:bg-slate-800 rounded-none h-12 font-manrope font-semibold uppercase tracking-wider text-sm"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </motion.div>

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <div className="bg-slate-900 p-8 sm:p-10">
                  <h2 className="font-oswald text-2xl font-semibold text-white mb-8">
                    Contact Information
                  </h2>
                  <div className="space-y-6">
                    {contactInfo.map((info) => (
                      <div key={info.label} className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-orange-500 flex items-center justify-center flex-shrink-0">
                          <info.icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-slate-400 text-sm font-manrope">
                            {info.label}
                          </p>
                          {info.href ? (
                            <a
                              href={info.href}
                              className="text-white font-manrope hover:text-orange-400 transition-colors block"
                            >
                              {info.value}
                            </a>
                          ) : (
                            <p className="text-white font-manrope">{info.value}</p>
                          )}
                          <p className="text-slate-400 text-sm font-manrope">
                            {info.subValue}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Map Placeholder */}
                <div className="relative h-64 bg-slate-200 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1674821770946-4f774b1907d7?w=800&q=80"
                    alt="Indian city view"
                    className="w-full h-full object-cover opacity-80"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-900/30">
                    <div className="bg-white px-6 py-4 flex items-center gap-3">
                      <MapPinIcon className="w-5 h-5 text-orange-500" />
                      <span className="font-manrope text-slate-900 font-medium">
                        Gurugram, Haryana
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quick Contact */}
                <div className="bg-orange-500 p-6 text-center">
                  <p className="text-white font-manrope mb-2">
                    Need immediate assistance?
                  </p>
                  <a
                    href="tel:+919560121484"
                    className="font-oswald text-2xl font-bold text-white hover:text-slate-100 transition-colors"
                  >
                    Call +91-9560121484
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </motion.div>
  );
};

export default ContactPage;
